/** Check out this flow chart for RelatedSlide machines
 * https://www.figma.com/board/pfUoEWSJvh2wxm38EOvygo/Related-Slide-State-Machine?node-id=0-1&p=f&t=FnTQ9lSFrH9OnMUJ-0
 */
import { createMachine } from 'xstate';
import { assign } from '@xstate/immer';
import { generateClient } from 'aws-amplify/api';
import { RelatedSlidesOutput } from '@alucio/aws-beacon-amplify/src/models';
import {
  RelatedSlidePageType,
  RelatedSlideSearchQuery,
  RelatedSlideSearchQueryVariables,
} from '@alucio/aws-beacon-amplify/src/API';
import { relatedSlideSearch } from '@alucio/aws-beacon-amplify/src/graphql/queries';
import type * as RelatedSlide from './relatedSlide.types';
import { StateTags } from './relatedSlide.types';
import { GenericError } from '../machineError';
import * as logger from 'src/utils/logger'

export const relatedSlideSM = createMachine(
  {
    id: 'RelatedSlideSM',
    tsTypes: {} as import('./relatedSlide.machine.typegen').Typegen0,
    predictableActionArguments: true,
    preserveActionOrder: true,
    schema: {
      context: {} as RelatedSlide.SMContext,
      events: {} as RelatedSlide.SMEvents,
      services: {} as RelatedSlide.SMServices,
    },
    context: {
      relatedSlides: {},
      currentPageId: undefined,
      error: undefined,
      retryAttempt: 0,
    },
    initial: 'idle',
    states: {
      idle: {
        description: 'Waiting for user to open the related slides tab.',
        tags: [StateTags.LOADING],
        on: {
          OPEN_RELATED_SLIDES_TAB: {
            target: 'determine',
          },
          SWITCH_PAGE: {
            actions: 'setCurrentPageId',
          },
        },
      },
      determine: {
        description: 'Determine if we need to fetch data or not.',
        tags: [StateTags.LOADING],
        always: [
          { cond: 'isOffline', target: 'offline' },
          { cond: 'isDataInContext', target: 'presenting' },
          { target: 'fetchRelatedSlides' },
        ],
        on: {
          SWITCH_OFFLINE: {
            target: 'offline',
          },
        },
      },
      fetchRelatedSlides: {
        description: 'fetching data from backend.',
        tags: [StateTags.LOADING],
        invoke: {
          src: 'fetchRelatedSlides',
          onDone: {
            actions: ['storeRelatedSlides', 'retryAttemptUpdate'],
            target: 'presenting',
          },
          onError: {
            actions: 'setError',
            target: 'error',
          },
        },
        on: {
          SWITCH_OFFLINE: {
            target: 'offline',
          },
        },
      },
      presenting: {
        description: 'Presenting related slides.',
      },
      offline: {
        description: 'User is currently offline, waiting for user to go online',
        on: {
          SWITCH_ONLINE: {
            target: 'determine',
          },
        },
      },
      error: {
        on: {
          USER_RETRY: {
            actions: 'retryAttemptUpdate',
            target: 'determine',
          },
        },
      },
    },
    on: {
      CLOSE_RELATED_SLIDES_TAB: {
        target: 'idle',
      },
      SWITCH_PAGE: {
        actions: 'setCurrentPageId',
        target: 'determine',
      },
    },
  },
  {
    guards: {
      isOffline: () => !navigator.onLine,
      isDataInContext: (ctx) => !!(ctx.currentPageId && ctx.relatedSlides[ctx.currentPageId]),
    },
    actions: {
      setCurrentPageId: assign((ctx, evt) => {
        logger.contentPresenting.CPM.relatedSlides.debug(`Set current pageId: ${evt.currentPageId}`);
        ctx.currentPageId = evt.currentPageId;
      }),
      storeRelatedSlides: assign((ctx, evt) => {
        if (!ctx.currentPageId) {
          logger.contentPresenting.CPM.relatedSlides.warn('Cannot find current pageId');
          return;
        }
        logger.contentPresenting.CPM.relatedSlides.debug('Storing related slide in context');
        ctx.relatedSlides[ctx.currentPageId] = evt.data.items.reduce<RelatedSlide.RelatedSlideData[]>((acc, item) => {
          if (item) {
            return [...acc, { documentVersionId: item.documentVersionId, pageId: item.pageId, score: item.score }]
          } else return acc;
        }, []);
      }),
      setError: assign((ctx, evt) => {
        logger.contentPresenting.CPM.relatedSlides.warn('Related slide machine error', evt.data);
        if (evt.data instanceof GenericError) {
          ctx.error = evt.data;
        } else {
          ctx.error = new GenericError('UNKNOWN_ERROR', 'Failed to set in machine');
        }
      }),
      retryAttemptUpdate: assign((ctx, evt) => {
        switch (evt.type) {
          case 'USER_RETRY': {
            logger.contentPresenting.CPM.relatedSlides.info(`User retry attempt #${ctx.retryAttempt + 1}`);
            ctx.retryAttempt++;
            break;
          }
          default: {
            ctx.retryAttempt = 0;
          }
        }
      }),
    },
    services: {
      fetchRelatedSlides: async (ctx) => {
        try {
          logger.contentPresenting.CPM.relatedSlides.info(`Start fetching related slide for: ${ctx.currentPageId}`);

          if (!ctx.currentPageId) {
            const errorText = 'Cannot find current pageId';
            logger.contentPresenting.CPM.relatedSlides.warn(errorText);
            throw new GenericError('UNKNOWN_ERROR', errorText)
          }

          const appsyncClient = generateClient<RelatedSlideSearchQuery>();
          const documentVersionId = ctx.currentPageId.split('_').slice(0, 2).join('_');
          const input: RelatedSlideSearchQueryVariables = {
            documentVersionId,
            pageId: ctx.currentPageId,
            pageTypes: [
              RelatedSlidePageType.COVER,
              RelatedSlidePageType.AGENDA,
              RelatedSlidePageType.CONTENT,
              RelatedSlidePageType.CLINICAL_DATA,
              RelatedSlidePageType.SAFETY_INFORMATION,
              RelatedSlidePageType.APPENDIX,
              RelatedSlidePageType.OTHER,
            ],
          };

          const { data, errors } = await appsyncClient.graphql({
            query: relatedSlideSearch,
            variables: input,
          });
          logger.contentPresenting.CPM.relatedSlides.debug('Related slide search response', data);
          if (errors?.length) throw new GenericError('UNKNOWN_ERROR', errors);
          const { relatedSlideSearch: responseData } = data;
          return responseData as RelatedSlidesOutput;
        } catch (e: any) {
          logger.contentPresenting.CPM.relatedSlides.warn(e);
          throw new GenericError(e);
        }
      },
    },
  },
)

export default relatedSlideSM;
